"use client";

// Client-side error capture. Three responsibilities:
//
//   1. Breadcrumbs: an in-memory ring buffer of the last ~50 user actions
//      (clicks, navigations, fetch calls). Auto-instrumented when init()
//      runs. Attached to every error reported via reportClientError.
//
//   2. Global listeners: window.error and unhandledrejection. Catches
//      everything that escapes React's error boundary — async crashes,
//      script errors, timer callbacks, etc.
//
//   3. Reporter: POSTs to /api/v1/errors/client with the captured shape.
//      Best-effort — uses keepalive so a navigation right after error
//      still ships the report.
//
// Initialized once at the root layout. Idempotent — calling init() twice
// is a no-op.

import type { Breadcrumb } from "./logger";

const MAX_BREADCRUMBS = 50;
const buffer: Breadcrumb[] = [];
let initialized = false;

export function addBreadcrumb(crumb: Omit<Breadcrumb, "ts">): void {
  buffer.push({ ...crumb, ts: Date.now() });
  if (buffer.length > MAX_BREADCRUMBS) buffer.shift();
}

export function getBreadcrumbs(): Breadcrumb[] {
  return buffer.slice();
}

// Single user-facing entry point. The error boundary, window listeners
// and any manual code path all funnel through here.
export function reportClientError(input: {
  error: Error | string;
  context?: Record<string, unknown>;
  // For the React error boundary — component stack from React.
  componentStack?: string;
}): void {
  try {
    const err = input.error;
    const message =
      typeof err === "string"
        ? err
        : err.message ?? "Unknown error";
    const stack =
      typeof err === "string"
        ? undefined
        : err.stack;
    const name =
      typeof err === "string"
        ? "Error"
        : err.name ?? err.constructor?.name ?? "Error";

    const payload = {
      name,
      message,
      stack,
      url: typeof window !== "undefined" ? window.location.pathname : undefined,
      breadcrumbs: getBreadcrumbs(),
      context: {
        ...(input.context ?? {}),
        ...(input.componentStack ? { componentStack: input.componentStack } : {}),
      },
    };

    // keepalive lets the request survive a page navigation right after
    // the error fires — common case is the user clicks something that
    // crashes and immediately reloads.
    fetch("/api/v1/errors/client", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      keepalive: true,
    }).catch(() => {
      // Swallow — error reporting that errors itself is just noise.
    });
  } catch {
    /* never throw from the reporter */
  }
}

// Initialize global listeners + auto-instrumentation. Call once from
// the root layout's client effect.
export function initClientErrorCapture(): void {
  if (initialized) return;
  if (typeof window === "undefined") return;
  initialized = true;

  // ── Global error listeners ───────────────────────────────────────────
  window.addEventListener("error", (e) => {
    // Some script errors arrive with no `.error` (CORS-blocked cross-
    // origin scripts). Best-effort using whatever fields are populated.
    const err =
      e.error instanceof Error
        ? e.error
        : new Error(e.message || "Unknown window error");
    reportClientError({
      error: err,
      context: {
        source: "window.error",
        filename: e.filename,
        line: e.lineno,
        col: e.colno,
      },
    });
  });

  window.addEventListener("unhandledrejection", (e) => {
    const reason = e.reason;
    const err =
      reason instanceof Error
        ? reason
        : new Error(typeof reason === "string" ? reason : JSON.stringify(reason));
    reportClientError({
      error: err,
      context: { source: "unhandledrejection" },
    });
  });

  // ── Breadcrumb auto-instrumentation ─────────────────────────────────
  // Navigation: history.pushState / popstate.
  const origPush = window.history.pushState.bind(window.history);
  window.history.pushState = function (data, unused, url) {
    addBreadcrumb({
      kind: "navigation",
      data: { to: typeof url === "string" ? url : url?.toString() ?? "?" },
    });
    return origPush(data, unused, url);
  };
  window.addEventListener("popstate", () => {
    addBreadcrumb({ kind: "navigation", data: { to: window.location.pathname } });
  });

  // Fetch instrumentation: log method + url + status. Body is NOT
  // captured — would balloon the breadcrumb size and may contain PII.
  const origFetch = window.fetch.bind(window);
  window.fetch = async (input, init) => {
    const method = (init?.method ?? "GET").toUpperCase();
    const url =
      typeof input === "string"
        ? input
        : input instanceof URL
        ? input.toString()
        : input.url;
    const startedAt = Date.now();
    try {
      const res = await origFetch(input, init);
      addBreadcrumb({
        kind: "fetch",
        data: {
          method,
          url,
          status: res.status,
          durationMs: Date.now() - startedAt,
        },
      });
      return res;
    } catch (err) {
      addBreadcrumb({
        kind: "fetch",
        data: {
          method,
          url,
          error: err instanceof Error ? err.message : String(err),
          durationMs: Date.now() - startedAt,
        },
      });
      throw err;
    }
  };

  // UI clicks: target tag + a short text label. Limited to keep the
  // breadcrumb compact. Skips text inputs / passwords (potential PII).
  document.addEventListener(
    "click",
    (e) => {
      const t = e.target as HTMLElement | null;
      if (!t) return;
      const tag = t.tagName?.toLowerCase() ?? "?";
      // Skip inputs to avoid logging keystrokes / sensitive fields. Click
      // on a button/a is the meaningful UX signal.
      if (tag === "input" || tag === "textarea") return;
      const text = (t.textContent ?? "").trim().slice(0, 32);
      addBreadcrumb({
        kind: "ui.click",
        data: {
          tag,
          text,
          // role/aria-label helps for icon-only buttons where text is empty.
          role: t.getAttribute("role") ?? undefined,
          ariaLabel: t.getAttribute("aria-label") ?? undefined,
        },
      });
    },
    { capture: true },
  );
}
